<template>
  <v-row justify="center">
    <v-dialog v-model="setdialog" width="600" persistent>
      <v-card>
        <v-card-title class="headline pl-6 pt-4"
          >Draw your signature</v-card-title
        >
        <v-card-text class="pt-2">
          <div class="signature-container" ref="signatureContainer">
            <canvas ref="mySignature"></canvas>
            <div class="placeholder-text" v-if="!hasSignature">Sign here</div>
          </div>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4">
          <v-btn text @click="setdialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="secondary"
            @click="clearCanvas"
            :disabled="!hasSignature"
            >Clear</v-btn
          >
          <v-btn
            color="primary"
            :disabled="!hasSignature"
            @click="saveSignature"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import SignaturePad from "signature_pad";

export default {
  name: "SignaturePad",
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      signaturePad: null,
      hasSignature: false,
    };
  },
  computed: {
    setdialog: {
      get() {
        return this.dialog;
      },
      set(val) {
        this.$emit("closeSignatureDialog", val);
      },
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        // Wait for Vuetify dialog transition to finish (approx 300ms)
        setTimeout(() => {
          this.initPad();
        }, 300);
      }
    },
  },
  mounted() {
    if (this.dialog) {
      setTimeout(() => {
        this.initPad();
      }, 300);
    }
    window.addEventListener("resize", this.resizeCanvas);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCanvas);
    if (this.signaturePad) {
      this.signaturePad.off();
      this.signaturePad = null;
    }
  },
  methods: {
    initPad() {
      const canvas = this.$refs.mySignature;
      if (!canvas) return;

      // Prevent re-initialization if already exists (unless we want to reset)
      // But for dialogs, we often want to ensure dimensions are fresh.
      if (this.signaturePad) {
        this.resizeCanvas();
        return;
      }

      this.signaturePad = new SignaturePad(canvas, {
        minWidth: 1,
        maxWidth: 2.5,
        penColor: "rgb(66, 133, 244)", // Google Blue
        backgroundColor: "rgba(255, 255, 255, 0)",
      });

      // Handle touch action to prevent scrolling on mobile
      canvas.style.touchAction = "none";

      this.resizeCanvas();

      // Listen to events to update UI state
      this.signaturePad.addEventListener("endStroke", () => {
        this.updateSignatureState();
      });
    },
    updateSignatureState() {
      if (this.signaturePad) {
        this.hasSignature = !this.signaturePad.isEmpty();
      }
    },
    resizeCanvas() {
      const canvas = this.$refs.mySignature;
      // If canvas is hidden or not laid out yet, offsetWidth will be 0.
      if (!canvas || canvas.offsetWidth === 0) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);

      // Reset signature pad state because changing canvas size clears it
      if (this.signaturePad) {
        this.signaturePad.clear();
        this.hasSignature = false;
      }
    },
    clearCanvas() {
      if (this.signaturePad) {
        this.signaturePad.clear();
        this.updateSignatureState();
      }
    },
    saveSignature() {
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        let data = this.signaturePad.toDataURL();
        if (data) {
          data = data.split(",")[1];
          this.$emit("signatureData", data);
          this.$emit("closeSignatureDialog", false);
        }
      }
    },
  },
};
</script>

<style scoped>
.signature-container {
  position: relative;
  width: 100%;
  height: 300px; /* Increased height for better usability */
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
  overflow: hidden;
}

canvas {
  width: 100%;
  height: 100%;
  display: block;
  cursor: crosshair;
}

.placeholder-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #bdbdbd;
  font-size: 1.5rem;
  font-weight: 500;
  pointer-events: none;
  user-select: none;
  text-transform: uppercase;
  letter-spacing: 1px;
}
</style>
